import type {
  DndContextType,
  DropCallback,
  DropEnterCallback,
  DropExitCallback,
  RegisterDndCallbacks,
  UseDndCallbacks,
} from "@/components/drag-and-drop/dnd-provider.types";
import React, { createContext, useContext, useEffect, useRef } from "react";
import { DropZone, DropZoneProps } from "react-aria-components";

/**
 * PageDragAndDropProvider is a React context provider that manages page-wide drag and drop functionality
 * across the application. It enables components anywhere in the page to handle drag and drop events,
 * with a priority system to determine which component should handle the event.
 *
 * How it works:
 * 1. Components can register callbacks for page-wide drop, dropEnter, and dropExit events using the usePageDragAndDrop hook
 * 2. Each registration gets a unique ID and priority level
 * 3. When a drag event occurs anywhere on the page, the provider selects the highest priority callback to handle it
 * 4. If multiple callbacks have the same priority, the most recently registered one wins
 *
 * Usage:
 * Use the usePageDragAndDrop hook in components that need to handle page-wide drag and drop:
 *  const { register } = usePageDragAndDrop({
 *    priority: 10,
 *    onDrop: (e) => handleDrop(e),
 *    onDropEnter: () => setIsDragOver(true),
 *    onDropExit: () => setIsDragOver(false)
 *  });
 *
 * The provider uses react-aria-components' DropZone under the hood for accessibility
 * and cross-browser compatibility, enabling drag and drop functionality across the entire page.
 */

/**
 * Internal helper – selects the callback with the highest priority.
 * If multiple callbacks share the highest priority, the most recently
 * registered one (largest `id`) wins.
 */
function getHighestPriorityCallback<T extends DropCallback | DropEnterCallback | DropExitCallback>(
  callbacks: Array<{ id: number; priority: number; callback: T | null }>
): T | null {
  if (callbacks.length === 0) return null;

  let winner = callbacks[0];
  for (const cb of callbacks) {
    if (cb.priority > winner.priority || (cb.priority === winner.priority && cb.id > winner.id)) {
      winner = cb;
    }
  }
  return winner.callback ?? null;
}

const DndContext = createContext<DndContextType | undefined>(undefined);

interface DndProviderProps extends DropZoneProps {}

export const PageDragAndDropProvider: React.FC<DndProviderProps> = props => {
  const globalRegistrationCounter = useRef(0);

  const onDropCallbacksRef = useRef<
    Array<{ id: number; priority: number; callback: DropCallback }>
  >([]);
  const onDropEnterCallbacksRef = useRef<
    Array<{ id: number; priority: number; callback: DropEnterCallback }>
  >([]);
  const onDropExitCallbacksRef = useRef<
    Array<{ id: number; priority: number; callback: DropExitCallback }>
  >([]);

  const register = ({
    priority,
    onDrop,
    onDropEnter,
    onDropExit,
  }: RegisterDndCallbacks): number => {
    const id = ++globalRegistrationCounter.current;

    if (onDrop) {
      onDropCallbacksRef.current.push({ id, priority, callback: onDrop });
    }
    if (onDropEnter) {
      onDropEnterCallbacksRef.current.push({ id, priority, callback: onDropEnter });
    }
    if (onDropExit) {
      onDropExitCallbacksRef.current.push({ id, priority, callback: onDropExit });
    }

    return id;
  };

  const unregister = (id: number) => {
    const filterFn = (item: { id: number }) => item.id !== id;
    onDropCallbacksRef.current = onDropCallbacksRef.current.filter(filterFn);
    onDropEnterCallbacksRef.current = onDropEnterCallbacksRef.current.filter(filterFn);
    onDropExitCallbacksRef.current = onDropExitCallbacksRef.current.filter(filterFn);
  };

  const contextValue: DndContextType = {
    register,
    unregister,
  };

  const dropZoneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = dropZoneRef.current;
    if (!element) return;

    // React Aria's DropZone has a hidden button that uses useClipboard hook.
    // The useClipboard hook only activates when that button is focused.
    // We prevent the button from ever being focused by making it inert.
    // And all that hacky stuff is to prevent Drag and Drop from hijacking Paste event
    // (which is handled by another mechanism)
    const button = element.querySelector("button");
    if (button) {
      // Remove from tab order so it can't be focused
      button.setAttribute("tabindex", "-1");
      // Prevent programmatic focus as well
      const originalFocus = button.focus;
      button.focus = () => {
        // Do nothing - prevent focus
      };

      return () => {
        button.focus = originalFocus;
      };
    }
  }, []);

  return (
    <DndContext.Provider value={contextValue}>
      <div ref={dropZoneRef}>
        <DropZone
          {...props}
          onDrop={event => {
            const cb = getHighestPriorityCallback(onDropCallbacksRef.current);
            cb?.(event);
          }}
          onDropEnter={event => {
            const cb = getHighestPriorityCallback(onDropEnterCallbacksRef.current);
            cb?.(event);
          }}
          onDropExit={event => {
            const cb = getHighestPriorityCallback(onDropExitCallbacksRef.current);
            cb?.(event);
          }}
        />
      </div>
    </DndContext.Provider>
  );
};

/**
 * Hook for registering drag and drop callbacks with priority-based handling.
 *
 * @param callbacks - Optional configuration object containing:
 *   - onDrop: Callback function triggered when items are dropped
 *   - onDropEnter: Callback function triggered when items enter the drop zone
 *   - onDropExit: Callback function triggered when items exit the drop zone
 *   - priority: Optional number determining callback precedence (higher numbers take priority)
 *   - isActive: Optional boolean to conditionally toggle off usage of the hook. Set to false when your component should not handle these events.
 *
 * You do NOT need to handle unregistering the callbacks yourself. The hook automatically un-registers the callbacks when your component unmounts.
 *
 * @returns The DnD context containing register/unregister functions
 *
 * @example
 * ```tsx
 * usePageDragAndDrop({
 *   priority: 10,
 *   onDrop: (items) => handleDrop(items),
 *   onDropEnter: () => setIsDragOver(true),
 *   onDropExit: () => setIsDragOver(false)
 * });
 * ```
 */

export const usePageDragAndDrop = (callbacks?: UseDndCallbacks): DndContextType => {
  const context = useContext(DndContext);
  if (context === undefined) {
    throw new Error("usePageDragAndDrop must be used within a PageDragAndDropProvider");
  }

  const { onDrop, onDropEnter, onDropExit, priority = 0, isActive = true } = callbacks || {};

  const registrationIdRef = useRef<number | null>(null);

  useEffect(() => {
    // Register only if at least one callback is provided and active is true
    if (isActive && (onDrop || onDropEnter || onDropExit)) {
      // If we've already registered, unregister first to avoid duplicates
      if (registrationIdRef.current !== null) {
        context.unregister(registrationIdRef.current);
      }

      registrationIdRef.current = context.register({
        priority,
        onDrop: onDrop || null,
        onDropEnter: onDropEnter || null,
        onDropExit: onDropExit || null,
      });
    } else if (registrationIdRef.current !== null) {
      // If not active but previously registered, unregister
      context.unregister(registrationIdRef.current);
      registrationIdRef.current = null;
    }

    // Always return a cleanup function
    return () => {
      if (registrationIdRef.current !== null) {
        context.unregister(registrationIdRef.current);
        registrationIdRef.current = null;
      }
    };
  }, [context, isActive, onDrop, onDropEnter, onDropExit, priority]);

  return context;
};
